import { useCallback, useEffect, useState } from 'react';
import type {
  ProfileProps,
  RecommendationProfileProviderProps,
  UpdateProfileProps,
} from './interfaces';
import { RecommendationProfileContext } from '.';
import { useRecommendationChannel } from '@/context/channels/recommendation';
import { useNotification } from '@/context/notification';
import {
  useCompleteRecommendationProfile,
  useRecommendationProfile,
  useUpdateRecommendationProfile,
} from '@/data/recommendation/profiles';
import { useUser } from '@/data/user';
import { ProfileStepIds } from '@/models/recommendation';

export const RecommendationProfileProvider = ({
  children,
}: RecommendationProfileProviderProps) => {
  const [isProfileQuizOpen, setIsProfileQuizOpen] = useState(false);
  const [iaBuilderActive, setIaBuilderActive] = useState(false);
  const { user } = useUser();
  const { profile, isLoading: isProfileLoading } = useRecommendationProfile();
  const { mutate: updateRecommendationProfile, isPending: isUpdating } =
    useUpdateRecommendationProfile();
  const { mutate: completeRecommendationProfile, isPending: isCompleting } =
    useCompleteRecommendationProfile();
  const { subscribeRecommendation } = useRecommendationChannel();
  const { toast } = useNotification();

  const completeOnboarding = useCallback(
    ({ profile }: ProfileProps) => {
      completeRecommendationProfile(
        { ...profile, current_step: ProfileStepIds.PresentationFinished },
        {
          onSuccess: () => {
            subscribeRecommendation();
            setIsProfileQuizOpen(false);
            setIaBuilderActive(true);
          },
        }
      );
    },
    [completeRecommendationProfile, subscribeRecommendation]
  );

  const updateProfile = useCallback(
    ({ profile }: UpdateProfileProps) => {
      updateRecommendationProfile(profile, {
        onSuccess: () => {
          if (profile?.current_step) return;
          toast({
            content: 'Perfil atualizado com sucesso',
            type: 'success',
          });

          // Atualmente esse trecho não funcionaria pq a atualização do perfil não trigga alteração nenhuma de recomendações, mas isso será revisto com as recomendações em tópico
          // https://galenagrupo.slack.com/archives/C023AF741FA/p1770380159234069
          // if (skipRecommendationsUpdate) return;
          // subscribeRecommendation();
        },
      });
    },
    [updateRecommendationProfile, toast]
  );

  useEffect(() => {
    if (isProfileLoading || !profile) return;

    const openProfileQuiz =
      user && user?.terms_accepted && profile.completed_at === null;

    if (openProfileQuiz) {
      setIsProfileQuizOpen(true);
    }
  }, [profile, user, isProfileLoading]);

  return (
    <RecommendationProfileContext.Provider
      value={{
        completeOnboarding,
        updateProfile,
        setIaBuilderActive,
        iaBuilderActive,
        isCompleting,
        isUpdating,
        profile,
        isProfileLoading,
        isProfileQuizOpen,
      }}
    >
      {children}
    </RecommendationProfileContext.Provider>
  );
};
